import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-3xl text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 font-body active:scale-[0.98]",
  {
    variants: {
      variant: {
        // Primary Blue - trust and navigation
        default: "bg-primary text-primary-foreground hover:bg-brand-blue-deep shadow-paper hover:shadow-paper-hover hover:-translate-y-0.5",
        
        // CTA Gold-Orange gradient - exclusive for main actions (Skapa saga, Köp)
        cta: "bg-gradient-to-r from-gold to-orange text-brand-blue-deep font-bold shadow-magic hover:shadow-magic-hover hover:-translate-y-1 hover:scale-[1.02]",
        
        // Destructive
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        
        // Outline with teal accent
        outline: "border-2 border-secondary/40 bg-card hover:bg-secondary/10 hover:border-secondary text-primary shadow-paper",
        
        // Secondary - soft teal
        secondary: "bg-secondary/15 text-primary hover:bg-secondary/25 shadow-paper",
        
        // Ghost for minimal UI
        ghost: "text-primary/80 hover:text-primary hover:bg-primary/10 rounded-xl text-base",
        
        // Link style
        link: "text-primary underline-offset-4 hover:underline",
        
        // Gold - for credits and premium features
        gold: "bg-gradient-to-r from-gold to-gold-warm text-brand-blue-deep font-bold shadow-paper hover:shadow-magic hover:-translate-y-0.5",
        
        // Soft - subtle background
        soft: "bg-muted text-primary hover:bg-muted/80 border border-border/50 shadow-paper",
        
        // Navigation button
        nav: "text-primary/80 hover:text-primary hover:bg-primary/10 font-medium rounded-xl text-base",
        
        // Hero - larger prominent button with brand gradient (teal to blue like logo)
        hero: "bg-gradient-to-r from-secondary to-primary text-white shadow-paper-hover hover:shadow-elevated hover:-translate-y-1 text-base font-bold",
        
        // Magic - legacy support (maps to CTA)
        magic: "bg-gradient-to-r from-gold to-orange text-brand-blue-deep shadow-magic hover:shadow-magic-hover hover:-translate-y-1 font-bold",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 rounded-2xl px-4",
        lg: "h-12 rounded-3xl px-8 text-base",
        xl: "h-14 rounded-[28px] px-10 text-lg",
        icon: "h-10 w-10 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
